import { getAuthHeader } from "@/components/document-chat/utils";
import { ChatStatus, StreamPhase } from "@/constants/enums";
// Types are globally available from @/types/chat-sse.d.ts
import type { DocumentChatRuntime } from "../types";
import type { TFunction } from "i18next";

/**
 * Creates the fetch request and initializes the stream reader
 *
 * @param endpoint - The SSE endpoint URL
 * @param search - URL search parameters
 * @param controller - Abort controller for cancellation
 * @param session - The current stream session
 * @param runtime - The document chat runtime store interface
 * @param getAuthToken - Optional function to get auth token
 * @param t - Translation function
 * @returns The initialized stream reader
 */
export async function initializeStream(
  endpoint: string,
  search: URLSearchParams,
  controller: AbortController,
  session: StreamSession,
  runtime: DocumentChatRuntime,
  getAuthToken?: () => string | null | undefined,
  t?: TFunction
): Promise<ReadableStreamDefaultReader<string>> {
  const response = await fetch(`${endpoint}?${search.toString()}`, {
    method: "GET",
    headers: {
      Accept: "text/event-stream",
      Authorization: getAuthHeader(getAuthToken)
    },
    signal: controller.signal
  });

  if (!response.ok) {
    const status = response.status;
    const errorMessage = t
      ? t("documentchat.streamedresponse.error.serviceunavailable", { status })
      : `Service unavailable: ${status}`;
    throw new Error(errorMessage);
  }
  if (!response.body) {
    const errorMessage = t
      ? t("documentchat.streamedresponse.error.nostreamssupport")
      : "Stream not supported";
    throw new Error(errorMessage);
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  session.reader = reader as unknown as ReadableStreamDefaultReader<string>;

  // Initialize stream state
  runtime.update({ status: ChatStatus.OPEN, phase: StreamPhase.THINKING, thinkingText: "" });
  runtime.markThinkingStart();

  return reader;
}

/**
 * Cleans up stream resources
 *
 * @param session - The current stream session
 */
export async function cleanupStream(session: StreamSession): Promise<void> {
  try {
    await session.reader?.cancel?.();
  } catch {
    // ignore
  }
  session.reader = null;
}

/**
 * Handles stream errors and sets appropriate status/error state
 * Enhances error messages for common network issues to provide better user feedback
 *
 * @param e - The error that occurred
 * @param session - The current stream session
 * @param runtime - The document chat runtime store interface
 * @param t - Translation function
 */
export function handleStreamError(
  e: unknown,
  session: StreamSession,
  runtime: DocumentChatRuntime,
  t: TFunction
): void {
  // If abortController was nulled by cancel(), treat as user cancel
  if (!session.abortController) {
    runtime.update({ status: ChatStatus.CLOSED });
    return;
  }

  // If the request was aborted, treat as user cancel
  if (session.abortController.signal.aborted) {
    runtime.update({ status: ChatStatus.CLOSED });
    return;
  }

  // Enhance error messages for common issues
  let message: string;

  if (e instanceof Error) {
    if (e.name === "TypeError" && e.message?.includes("fetch")) {
      message = t("documentchat.streamedresponse.error.connectionerror");
    } else if (e.name === "AbortError") {
      message = t("documentchat.streamedresponse.error.requestcancelled");
    } else {
      message = e.message;
    }
  } else {
    message = t("documentchat.streamedresponse.error.unknownerror");
  }

  runtime.update({ status: ChatStatus.ERROR, error: message });
}
